import * as mode from './gcMode';
import * as gcService from '@greycat/lang-vscode-languageservice';

export { ParseResult, GreyCatType, GreyCatFunction } from '@greycat/lang-vscode-languageservice';

import Emitter = monaco.Emitter;
import IEvent = monaco.IEvent;
import IDisposable = monaco.IDisposable;
import GreyCatType = gcService.GreyCatType;
import GreyCatFunction = gcService.GreyCatFunction;

export interface Settings {
  readonly lint?: boolean;
  readonly lintOptions?: {
    readonly strict?: boolean;
  };
}

export interface LanguageServiceDefaults {
  readonly onDidChange: IEvent<LanguageServiceDefaults>;
  readonly settings: Settings;
  setSettings(settings: Settings): void;
  addTypes(types: GreyCatType[]): void;
  addFunctions(functions: GreyCatFunction[]): void;
}

// --- GreyCat configuration and defaults ---------
const DEFAULT_SETTINGS: Settings = {
  lint: true,
  lintOptions: {
    strict: true,
  },
};

export class LanguageServiceDefaultsImpl implements LanguageServiceDefaults {
  private _onDidChange = new Emitter<LanguageServiceDefaults>();
  private _settings: Settings;
  private _languageId: string;
  private _gcTypes: GreyCatType[] = [];
  private _gcFunctions: GreyCatFunction[] = [];

  constructor(languageId: string, settings: Settings) {
    this._languageId = languageId;
    this.setSettings(settings);
  }

  get onDidChange(): IEvent<LanguageServiceDefaults> {
    return this._onDidChange.event;
  }

  get languageId(): string {
    return this._languageId;
  }

  get settings(): Settings {
    return this._settings;
  }

  get gcTypes(): GreyCatType[] {
    return this._gcTypes;
  }

  get gcFunctions(): GreyCatFunction[] {
    return this._gcFunctions;
  }

  public setSettings(options: Settings): void {
    this._settings = { ...DEFAULT_SETTINGS, ...options };
    this._onDidChange.fire(this);
  }

  public addTypes(types: GreyCatType[]): IDisposable {
    this._gcTypes.push(...types);
    this._onDidChange.fire(this);
    return {
      dispose: () => {
        this._gcTypes.length = 0;
        this._onDidChange.fire(this);
      },
    };
  }

  public addFunctions(functions: GreyCatFunction[]): IDisposable {
    this._gcFunctions.push(...functions);
    this._onDidChange.fire(this);
    return {
      dispose: () => {
        this._gcFunctions.length = 0;
        this._onDidChange.fire(this);
      },
    };
  }
}

const defaults = new LanguageServiceDefaultsImpl('greycat', DEFAULT_SETTINGS);

// Export API
function createAPI(): LanguageServiceDefaults {
  return defaults;
}

export default ((monaco.languages as any).greycat = createAPI());

// --- Registration to monaco editor ---
function getMode(): monaco.Promise<typeof mode> {
  return monaco.Promise.wrap(import('./gcMode'));
}

monaco.languages.register({ id: 'greycat' });

monaco.languages.onLanguage('greycat', () => {
  return getMode().then((m) => m.setupMode(defaults));
});
